import { $isLinkNode } from '@lexical/link'
import { $isHeadingNode } from '@lexical/rich-text'
import { $getSelectionStyleValueForProperty } from '@lexical/selection'
import { $findMatchingParent, mergeRegister } from '@lexical/utils'
import {
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  type LexicalEditor
} from 'lexical'
import React from 'react'

import { FONT_SIZE } from '../constants'
import type { ToolbarStateProps } from '../text-editor.types'

const INITIAL_STATE: ToolbarStateProps = {
  canUndo: false,
  canRedo: false,
  blockType: 'paragraph',
  isBold: false,
  isItalic: false,
  isUnderline: false,
  isLink: false,
  fontSize: FONT_SIZE.default,
  elementFormat: 'left'
} as const

const readElementState = (
  editor: LexicalEditor
): Pick<ToolbarStateProps, 'blockType' | 'elementFormat'> =>
  editor.getEditorState().read(() => {
    const element = $getRoot().getFirstChild()
    if (!element)
      return { blockType: INITIAL_STATE.blockType, elementFormat: INITIAL_STATE.elementFormat }

    return {
      blockType: $isHeadingNode(element) ? element.getTag() : element.getType(),
      elementFormat: ($isElementNode(element) && element.getFormatType()) || 'left'
    }
  })

export const useToolbarState = (editor: LexicalEditor): ToolbarStateProps => {
  const [state, setState] = React.useState<ToolbarStateProps>(() => ({
    ...INITIAL_STATE,
    ...readElementState(editor)
  }))

  const $syncSelection = React.useCallback(() => {
    const selection = $getSelection()

    const anchorNode = $isRangeSelection(selection) ? selection.anchor.getNode() : null
    const element = anchorNode
      ? ($findMatchingParent(anchorNode, (node) => {
          const parent = node.getParent()
          return parent !== null && $isRootOrShadowRoot(parent)
        }) ?? anchorNode.getTopLevelElementOrThrow())
      : $getRoot().getFirstChild()

    if (element) {
      const blockType = $isHeadingNode(element) ? element.getTag() : element.getType()
      const elementFormat = ($isElementNode(element) && element.getFormatType()) || 'left'
      setState((prev) => ({ ...prev, blockType, elementFormat }))
    }

    if (!$isRangeSelection(selection)) return

    const isBold = selection.hasFormat('bold')
    const isItalic = selection.hasFormat('italic')
    const isUnderline = selection.hasFormat('underline')

    const anchor = selection.anchor.getNode()
    const isLink = $isLinkNode(anchor) || $isLinkNode(anchor.getParent())

    const fontSizeValue = $getSelectionStyleValueForProperty(
      selection,
      'font-size',
      `${FONT_SIZE.default}px`
    )
    const fontSize = parseInt(fontSizeValue, 10) || FONT_SIZE.default

    setState((prev) => ({ ...prev, isBold, isItalic, isUnderline, isLink, fontSize }))
  }, [])

  React.useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => $syncSelection(), { editor })
      }),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (canUndo) => {
          setState((prev) => ({ ...prev, canUndo }))
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (canRedo) => {
          setState((prev) => ({ ...prev, canRedo }))
          return false
        },
        COMMAND_PRIORITY_LOW
      )
    )
  }, [editor, $syncSelection])

  return state
}
